/**
 * Defines a container for "documents", i.e. multi-instance widgets.  Features include
 *
 * <ol>
 *     <li>Tiling to view multiple documents simultaneously</li>
 *     <li>Tabbing to switch between documents within a tile</li>
 *     <li>Nested tile/tab arrangements</li>
 *     <li>Grouping of documents based on arbitrary facets</li>
 * </ol>
 *
 * Document grouping is optional but has several consequences:
 *
 * <ol>
 *     <li>Within a tile the tabs associated with documents belonging to a common group are kept together</li>
 *     <li>By default new documents appear in the same tile as the most recently selected existing group member</li>
 *     <li>Nested tiles/tabs, i.e. tiles and tabs within another tab, are homogeneous with respect to group</li>
 * </ol>
 *
 * The DocumentContainer's behavior is governed by a number of client settable properties.  These properties may be fixed,
 * meaning that they should be set at construction time and not changed or dynamic meaning that they can be
 * changed at any time.  Since the DocumentContainer inherits the "Stateful" mixin, get, set and watch methods
 * can be used to manage and track dynamic properties.
 *
 * @module MW/layout/DocumentContainer
 * @copyright Copyright 2013-2020 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/Deferred",
    "dojo/dom",
    "dojo/dom-attr",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/has",
    "dojo/mouse",
    "dojo/on",
    "dojo/Stateful",
    "dojo/touch",
    "dijit/focus",
    "dijit/registry",
    "mw-form/ContextMenu",
    "mw-form/Menu",
    "mw-form/MenuItem",
    "mw-form/MenuSeparator",
    "mw-form/PopupMenuItem",
    "mw-form/RadioButtonMenuItem",
    "MW/layout/ActionsMenu",
    "MW/layout/BorderContainer",
    "MW/layout/DocumentGroupProperties",
    "MW/layout/DocumentGroupRegistry",
    "MW/layout/Geometry",
    "MW/layout/GroupedTabContainer",
    "MW/layout/LayoutChildProperty",
    "MW/layout/LayoutContainerEvent",
    "MW/layout/LayoutUtils",
    "MW/layout/MaximizingContainer",
    "MW/layout/Restorable",
    "MW/layout/SelectionBlocker",
    "MW/layout/SelectionManager",
    "MW/layout/StatefulExtension",
    "MW/layout/TabContainer",
    "MW/layout/TabContainerLender",
    "MW/layout/TileContainer",
    "MW/layout/TileTabContainer",
    "MW/layout/Traversable",
    "mw-utils/allApprove",
    "MW/GridPicker",
    "mw-log/Log",
    "dojo/i18n!l10n/nls/layout"
], function (declare, event, lang, Deferred, dom, domAttr, domClass, domConstruct, domGeometry, domStyle, has, mouse, on,
             Stateful, touch, focus, registry, ContextMenu, Menu, MenuItem, MenuSeparator, PopupMenuItem, RadioMenuItem,
             ActionsMenu, BorderContainer, DocumentGroupProperties, DocumentGroupRegistry, Geometry, GroupedTabContainer,
             LayoutChildProperty, LayoutContainerEvent, LayoutUtils, MaximizingContainer, Restorable, SelectionBlocker,
             SelectionManager, StatefulExtension, TabContainer, TabContainerLender, TileContainer, TileTabContainer,
             Traversable, allApprove, GridPicker, Log, layoutL10n) {

    /**
     * @class MW/layout/DocumentContainer
     * @classdesc Defines a nestable container for "documents"
     */
    var DocumentContainer = declare([TileTabContainer, Restorable, Traversable, MaximizingContainer, StatefulExtension], {
        /**
         * @property dragBetweenTilesEnabled {boolean} Specifies whether the user can move documents from one tile to
         *           another by dragging.
         * @property groupingFacet {String} Indicates which document property (facet) should be used to group
         *           documents within the container.  Optional: If not supplied then documents will not be
         *           grouped.
         * @property groupRegistry {DocumentGroupRegistry} Provides access to document group properties.  Optional: Only
         *           needed if a non-empty groupingFacets array has been supplied
         * @property userTilingEnabled {boolean} specifies whether the user should be able to tile the container.
         *           Note that setting this false does not prevent programmatic tiling.
         * @property offerMaximizeButton {boolean} specifies whether a "maximize" button will appear in the upper-right
         *           corner of documents.  By default, if this property is not specified, a maximize button will appear
         *           only when no tab is displayed for the document (see the showSingleDocumentTab property).
         *           The reasoning here is that the button is needed in this case because the tab double click gesture,
         *           which normally triggers maximize, is unavailable.  If this property is specified then the maximize
         *           button will either always appear (if true) or never appear (if false), regardless of the presence of tabs.
         * @property restrictToSingleSplit {boolean} specifies whether the user should be able to arrange the container to
         *           have more than a single split hence limiting the container to two tiles.  This property does not prevent
         *           programmatic introduction of more than two tiles.
         * @property showSingleDocumentTab {boolean} specifies whether tab containers that contain only a single document
         *           within a single tile should display a tab for the document.
         * @property tileByDragEnabled {boolean} Specifies whether the user can crate or eliminate tiles by dragging.
         *           When false children can still be dragged from one existing tile to another, provided
         *           dragBetweenTileEnabled is true.
         *
         * @instance
         */
        Fixed: undefined,  // Trick to make the JSDoc generator list the above under "Fixed"
        showSingleDocumentTab: true,

        /**
         * @property placeHolderText {string} text that will appear in the container when no documents are open
         * @property tabPositon {string} location of the tabs ("top", "bottom", "left" or "right")
         * @instance
         */
        Dynamic: undefined,  // Trick to make the JSDoc generator list the above under "Dynamic"
        tabPosition: "top",

        tabContainerClass: GroupedTabContainer,
        baseClass: "documentContainer",
        _selectionCount: 0,
        _subContainerCount: 0,
        _maximizeUpdateDepth: 0,

        // _containedGroupId

        constructor: function () {
            this._selectionOrder = {};
            this._lastRegionForGroup = {};
            this._groupWatches = {};
        },

        postMixInProperties: function () {
            var that = this;
            this.inherited(arguments);

            delete this.Dynamic; // Not a real property just a JSDoc trick

            // Grandfather "typeInfo" in place of GroupRegistry
            if (!this.groupRegistry) {
                this.groupRegistry = new DocumentGroupRegistry();
            }
            if (this.typeInfo) {
                this.groupingFacet = "documentType";
                Object.keys(this.typeInfo).forEach(function (type) {
                    that.groupRegistry.register("documentType", type, new DocumentGroupProperties(that.typeInfo[type]));
                });
            }

            if (!this._isChildContainer) {
                if (!this.selectionManager) {
                    this.selectionManager = new SelectionManager();
                }
                this.selectionManager.registerContainer(this);
                this.own(this.selectionManager.watch(SelectionManager.SELECTED_CHILD, function (name, oldValue, newValue) {
                    var selectedGroupId;
                    if (that.containsDocument(newValue)) {
                        selectedGroupId = that._getGroupId(newValue);
                    }
                    that._updateSelectedGroup(selectedGroupId);
                }));
            }
            this.showSingleChildTab = this.showSingleDocumentTab;
        },

        postCreate: function () {
            var that = this;
            this.inherited(arguments);

            this._updateBackground();

            this.watch("animationEnabled", function (name, oldValue, newValue) {
                that._getChildContainers().forEach(function (container) {
                    container.set("animationEnabled", newValue);
                });
            });
            this.watch(TileContainer.GRID_SIZE, function (name, oldSize, newSize) {
                that._updateRegions();
                that.notifyStateWatchers(DocumentContainer.ARRANGEMENT, this._getArrangementFromGridSize(oldSize), this._getArrangementFromGridSize(newSize));
                if (newSize.w > 1 || newSize.h > 1) {
                    that._clearPreMaximizeState();
                }
            });
            var updatePlusActions = function () {
                that._getTabContainers().forEach(function (container) {
                    that._updatePlusActions(container);
                });
            };
            this.own(this.groupRegistry.on(DocumentGroupRegistry.PROPERTIES_ADDED, updatePlusActions));
            this.own(this.groupRegistry.on(DocumentGroupRegistry.PROPERTIES_REMOVED, updatePlusActions));

            // Add watches to the group properties of groups that support empty sub-containers
            this.groupRegistry.getIds(this.groupingFacet).forEach(function (groupId) {
                var groupProperties = that.groupRegistry.getProperties(that.groupingFacet, groupId);
                if (groupProperties.retainEmptySubContainer) {
                    that._addGroupWatches(groupId, groupProperties);
                }
            });
            // Also listen for new groups and obsolete groups
            this.own(this.groupRegistry.on(DocumentGroupRegistry.PROPERTIES_ADDED, function(facet, groupId, groupProperties) {
                if (facet == that.groupingFacet && groupProperties.retainEmptySubContainer) {
                    that._addGroupWatches(groupId, groupProperties);
                }
            }));
            this.own(this.groupRegistry.on(DocumentGroupRegistry.PROPERTIES_REMOVED, function(facet, groupId, groupProperties) {
                if (facet == that.groupingFacet && groupProperties.retainEmptySubContainer) {
                    that._removeGroupWatches(groupId);
                }
            }));
        },

        buildRendering: function () {
            this.inherited(arguments);
            if (!this._isChildContainer) {
                domClass.add(this.domNode, "rootContainer");
            }
            if (this._containedGroupId) {
                domClass.add(this.domNode, "groupContainer");
            }
            if (this.placeHolderText) {
                this._addPlaceHolder();
                this._setPlaceHolderText(this.placeHolderText);
            }
        },

        startup: function () {
            if (this._started) {
                return;
            }
            this.inherited(arguments);
            if (this._startingState) {
                this._restoreStartingState();
            }
        },

        destroyRecursive: function () {
            if (this.selectionManager) {
                this.selectionManager.unregisterContainer(this);
            }
            this.inherited(arguments);
        },

        addChild: function (widget) {
            var properties, groupId, groupProperties, subContainerState, pendingSubContainers,
                subContainers, subContainer, recent, i, region, showingChild, showingGroupId,
                showingRegion, splitTile;
            var tile = -1;
            if (!this._isInternalAdd) {
                if (this._startingState) {
                    this._restoreStartingState();
                }
                properties = this._getProperties(widget);
                if (!properties) {
                    properties = widget;
                }
                // Assign setId (used for grouping Tabs) from groupId
                if (this.groupingFacet) {
                    groupId = properties[this.groupingFacet];
                    properties.setId = groupId;
                    if (groupId) {
                        groupProperties = this.groupRegistry.getProperties(this.groupingFacet, groupId);
                        properties.__showHeader = groupProperties.showGroupHeader !== false;
                    }
                } else {
                    delete properties.setId;
                }
                if (!properties.childId) {
                    properties.childId = this._generateChildId(properties);
                }

                if (this._targetTile !== undefined) {
                    tile = this._targetTile;
                    delete this._targetTile;
                } else if (this._targetSubContainer) {
                    subContainer = this._targetSubContainer;
                    this._unbindChildProperties(widget, properties);
                    subContainer.addWithProperties(widget, properties);
                    properties.isInGroupTab = true;
                    delete this._targetSubContainer;
                } else if (groupId) {
                    properties.set(LayoutChildProperty.TITLE_SHORTENING_ALLOWED, !!groupProperties.get(DocumentGroupProperties.SHRINK_TABS_TO_FIT));
                    if (!this._isAddingBack) {
                        widget.own(groupProperties.watch(DocumentGroupProperties.SHRINK_TABS_TO_FIT, function (name, oldValue, newValue) {
                            properties.set(LayoutChildProperty.TITLE_SHORTENING_ALLOWED, newValue);
                        }));
                        this._addGroupWatches(groupId, groupProperties);
                    }
                    pendingSubContainers = this._pendingState ? (this._pendingState.nestings || this._pendingState.subContainers) : null;
                    if (this.isRestoringState() && pendingSubContainers) {
                        // See if the child should be placed in a sub-container.
                        for (i = 0; i < pendingSubContainers.length; i++) {
                            subContainerState = pendingSubContainers[i];
                            if (this._stateIncludesDocument(subContainerState, properties, groupProperties.constrainToSubContainer)) {
                                // Find the corresponding subContainer
                                subContainers = this.getGrandChildren(function (widget, properties) {
                                    var containerId = subContainerState.nestingId || subContainerState.containerId;
                                    return properties.childId === containerId;
                                });
                                if (subContainers.length > 0) {
                                    subContainer = subContainers[0];
                                    this._unbindChildProperties(widget, properties);
                                    subContainer.addWithProperties(widget, properties);
                                    properties.isInGroupTab = true;
                                    if (this.isRestoringState() && !this._areAnyTabContainersRestoringState()) {
                                        this.endStateRestore();
                                        break;
                                    }
                                }
                            }
                        }
                    } else if (this.getDocumentCount(groupId)) {
                        recent = this._getMostRecent(groupId);
                        if (recent.child && recent.child.isInstanceOf(DocumentContainer)) {
                            subContainer = recent.child;
                            this._unbindChildProperties(widget, properties);
                            subContainer.addWithProperties(widget, properties);
                            properties.isInGroupTab = true;
                        } else if (properties.tile === undefined) {
                            tile = recent.tile;
                        }
                    } else if (this.hasChildContainer(groupId)) {
                        subContainer = this._getChildContainers(groupId)[0];
                        this._unbindChildProperties(widget, properties);
                        subContainer.addWithProperties(widget, properties);
                    } else if (this._tileCount <= 2) {
                        region = this._getPreferredRegion(groupId);
                        if (this._tileCount == 1 && region !== undefined && region !== "center") {
                            // There is only a single tile.  If the preferred region of the child being added
                            // doesn't match that of the showing child then split the tile.
                            tile = 0;
                            showingChild = this.getChildInTile(0).get(TabContainer.SHOWING_CHILD);
                            if (showingChild) {
                                showingGroupId = this._getGroupId(showingChild);
                                showingRegion = this._getPreferredRegion(showingGroupId);
                                if (!this._areCorrespondingRegions(region, showingRegion)) {
                                    this._isSplittingForAdd = !groupProperties.suppressInitialFocus;
                                    tile = this._splitTile(0, region);
                                    this._isSplittingForAdd = false;
                                    splitTile = true;
                                }
                            }
                        } else {
                            tile = this._getTileFromRegion(region);
                        }
                    }
                } else if (properties.tile === undefined) {
                    // If not grouped and tile not specified, favor empty tile, then most recently selected among all tiles
                    tile = this._chooseTile();  // inherited method returns empty or last tile
                    if (tile >= 0 && !this.getChildInTile(tile).isEmpty()) {
                        tile = this._getMostRecent().tile;
                    }
                }
                // Create a sub-container and add the document to it if the document belongs to a group that is constrained
                // to reside in a sub-container or if this is the first document being added to a group for which a default
                // sub-tiling has been specified
                if (groupId && !subContainer && (groupProperties.constrainToSubContainer || (groupProperties.defaultTiling && !this.getDocumentCount(groupId)))) {
                    // Add a sub-container and then add the child to it
                    if (tile === undefined || tile < 0) {
                        tile = 0;
                    }
                    subContainer = this._addSubContainer(groupId, groupProperties, tile);
                    this._unbindChildProperties(widget, properties);
                    subContainer.addWithProperties(widget, properties);
                    properties.isInGroupTab = true;
                }

                if (tile !== undefined) {
                    [].splice.call(arguments, 1, 0, tile);
                }
            }

            if (subContainer) {
                if (!(this._isInternalAdd || this._isAddingBack)) {
                    // g2149206: Set a timeout to allow for the TabButton to be created before notifying downstream components.
                    setTimeout(function () {
                        this.emit(LayoutContainerEvent.CHILD_ADDED, widget, properties);
                    }.bind(this), 0);
                }
            } else {
                var isRestoring = this.isRestoringState();                
                this.inherited(arguments);
                if (properties && groupProperties && properties.get(LayoutChildProperty.IS_SELECTED)) {
                    groupProperties.setForWindow(window, DocumentGroupProperties.LAST_SELECTED, widget);
                }
                if (this._started && !isRestoring && properties && !(groupProperties && groupProperties.suppressInitialFocus)) {
                    properties.set(LayoutChildProperty.IS_SELECTED, true);
                }
                if (splitTile) {
                    this._updateRegions();
                }
            }
            if (!this._isInternalAdd) {
                this._updateBackground();
                this._updateRecentDocumentSizes();
            }
        },

        /**
         * @param groupId {string} group id (optional)
         * @returns {array} containing all documents belonging to the specified group or
         *                  all documents in the container if no group is specified
         * @instance
         */
        getDocuments: function (groupId) {
            var that = this;
            var documents = [];
            this._gatherDocuments(documents, function (document) {
                return !groupId || that._getGroupId(document) === groupId;
            });
            return documents;
        },

        /**
         * @param groupId {string} group id (optional)
         * @returns {Number} the number of documents in the group (if a group id is specified)
         *                   or the entire container (if no group id is specified)
         * @instance
         */
        getDocumentCount: function (groupId) {
            return this.getDocuments(groupId).length;
        },

        isEmpty: function () {
            return this.getDocumentCount() === 0;
        },

        containsDocument: function (widget) {
            return widget && dom.isDescendant(widget.domNode, this.domNode);
        },

        getShowingDocuments: function () {
            var that = this;
            var documents = [];
            this._gatherDocuments(documents, function (document) {
                var properties = that._getProperties(document);
                return properties && properties.isShowing;
            });
            return documents;
        },

        /**
         * @deprecated
         *
         * @param document {_Widget} the document whose region is sought
         * @instance
         */
        getContainingRegion: function (document) {
            if (this._tileCount === 1) {
                return "center";
            } else if (this._tileCount === 2) {
                var tile = this._getTileContainingChild(document);
                if (this.gridSize.w === 2) {
                    return tile ? "right" : "left";
                } else {
                    return tile ? "bottom" : "top";
                }
            } else {
                return "";
            }
        },

        /**
         * @deprecated Set document's TILE property instead
         *
         * @param document {_Widget} the document to move
         * @param region {string} the region to move it to
         * @instance
         */
        moveDocument: function (document, region) {
            this._getProperties(document).set(LayoutChildProperty.REGION, region);
        },

        /**
         * @deprecated
         * @instance
         */
        setPreferredRegionsToDefault: function () {
            this._lastRegionForGroup = {};
        },

        /**
         * Overridden to recursively remove all from sub-containers
         */
        removeAll: function (destroy) {
            this._getChildContainers().forEach(function (subContainer) {
                subContainer.removeAll(destroy);
            });
            this.inherited(arguments);
        },

        removeGroup: function (groupId) {
            this.getDocuments(groupId).forEach(lang.hitch(this, "removeChild"));
        },

        /**
         * Closes the specified document
         *
         * @param document {_Widget} the child to be closed
         * @instance
         */
        closeDocument: function (document, force) {
            TabContainerLender.getOwningTabContainer(document).closeChild(document, force);
        },

        /**
         * Closes all closable documents belonging to the specified group
         *
         * @instance
         */
        closeGroup: function (groupId) {
            this._closingGroupId = groupId;  // Prevents updates to _lastRegionForGroup
            var promise = this._closeDocuments(this.getDocuments(groupId));
            delete this._closingGroupId;
            return promise;
        },

        /**
         * Closes all closable documents in the container
         *
         * @instance
         */
        closeAll: function () {
            return this._closeDocuments(this.getDocuments());
        },

        /**
         * Similar to closeAll but does not skip non-closable documents and cleans up any tiling
         */
        clear: function () {
            this._closeDocuments(this.getDocuments(), true);
            this._eliminateEmptySubContainers();
            this.set(TileContainer.GRID_SIZE, {w: 1, h: 1});
        },

        refreshShowingDocuments: function () {
            var that = this;
            this._getTabContainers().forEach(function (container) {
                var showingChild = container.get(TabContainer.SHOWING_CHILD);
                if (showingChild) {
                    if (showingChild.isInstanceOf(DocumentContainer)) {
                        showingChild.refreshShowingDocuments();
                    } else {
                        that._getProperties(showingChild).set(LayoutChildProperty.IS_SHOWING, true);
                    }
                }
            });
        },

        /*
         * Part of the MaximizingContainer interface
         */
        canMaximizeLocally: function (child) {
            // May need to return a different result when populating the context menu.  If another child is maximized
            // the context menu for this child should still offer "restore".  On the other hand if this child's "isMaximized"
            // property is set programmatically the response should simply be to select the child.
            return (
                !this._isRestoringFromMaximize &&
                (
                    !!this._isMaximizing ||
                    !this._getProperties(child).get(LayoutChildProperty.IS_MAXIMIZED)
                ) &&
                (
                    this._tileCount > 1 ||
                    (
                        this._getProperties(child).get(LayoutChildProperty.IS_SHOWING) &&
                        !this._isPopulatingMenu
                    )
                )
            );
        },

        /*
         * Part of the MaximizingContainer interface
         */
        canRestoreLocally: function (child) {
            let preMaximizeTileCount = 0;
            if (this._preMaximizeState) {
                preMaximizeTileCount = this._preMaximizeState.tileCount;
            }
            return (
                !this._isMaximizing &&
                (
                    !!this._isRestoringFromMaximize ||
                    this._getProperties(child).get(LayoutChildProperty.IS_MAXIMIZED)
                ) &&
                (
                    (preMaximizeTileCount > 1) ||
                    (
                        this._preMaximizeState &&
                        this._preMaximizeState.nestings &&
                        this._preMaximizeState.nestings.length > 0
                    ) ||
                    (
                        !!this._getGroupProperties(child, this._getProperties(child)).panelIds &&
                        !!this._preMaximizeState
                    )
                )
            );
        },

        /*
         * Part of the MaximizingContainer interface
         */
        maximizeLocally: function (child) {
            var rootContainer;
            var properties = this._getProperties(child);
            if (this._tileCount > 1) {
                this._isChangingTiling = true;
                if (this._isChildContainer) {
                    // If a sub-container is being maximized the root container should save its state
                    // Since the sub-container may be removed.
                    rootContainer = this._getRootContainer();
                    if (!rootContainer._preMaximizeState) {
                        rootContainer._preMaximizeState = rootContainer.getState();
                    }
                } else if (!this._preMaximizeState) {
                    this._preMaximizeState = this.getState();
                }
                this.set(TileContainer.GRID_SIZE, {w: 1, h: 1});
            }
            properties.set(LayoutChildProperty.IS_SHOWING, true);
            setTimeout(function () {
                properties.set(LayoutChildProperty.IS_SELECTED, true);
            }, 0);
            this._isChangingTiling = false;
        },

        /*
         * Part of the MaximizingContainer interface
         */
        restoreLocally: function (child) {
            if (this._preMaximizeState) {
                this._isChangingTiling = true;
                this.setState(this._preMaximizeState);
                this.endStateRestore();
                delete this._preMaximizeState;
                this._isChangingTiling = false;
            }
        },

        _subTileGroup: function (groupId, gridSize) {
            var members;
            var subContainers = this._getChildContainers(groupId);
            if (subContainers.length > 0) {
                // Already in a sub-container, just update its grid.
                subContainers[0].set(TileContainer.GRID_SIZE, gridSize);
            } else if (gridSize.w > 1 || gridSize.h > 1) {
                // Gather all group members and extract to sub-container
                members = this.getDocuments(groupId);
                if (members.length) {
                    this._extractToSubContainer(members, this._getTileContainingChild(members[0]), gridSize);
                }
            }
        },

        _moveGroup: function (groupId, tile) {
            var that = this;
            var subContainers = this._getChildContainers(groupId);
            if (subContainers.length > 0) {
                // Group is in a sub-container.  Move the container
                this._moveChild(subContainers[0], this._getTileContainingChild(subContainers[0]), tile, true);
            } else {
                // Move all group members to specified tile
                this.getDocuments(groupId).forEach(function (document) {
                    that._getProperties(document).set(LayoutChildProperty.TILE, tile);
                });
            }
            this.eliminateEmptyTiles();
        },

        /**
         * Determines the next child relative to the given reference child.  This is part of the Traversable API.
         *
         * @param child {_Widget} the reference child widget
         * @param showing {boolean} if true then return a child that is already showing
         * @returns {_Widget} the next child widget
         * @instance
         */
        nextChild: function (child, showing) {
            if (this.domNode.classList.contains("hideAllChildren")) {
                // The container is hidden
                return null;
            }
            var tile = child ? (this.getTileContaining(child) + 1) : 0;
            return tile < this._tileCount ? this.getChildInTile(tile) : null;
        },

        /**
         * Determines the previous child relative to the given reference child.  This is part of the Traversable API.
         *
         * @param child {_Widget} the reference child widget
         * @param showing {boolean} if true then return a child that is already showing
         * @returns {_Widget} the previous child widget
         * @instance
         */
        previousChild: function (child, showing) {
            var tile = child ? (this.getTileContaining(child) - 1) : this._tileCount - 1;
            return tile >= 0 ? this.getChildInTile(tile) : null;
        },

        getState: function (crossSession) {
            var that = this;
            var state = this.inherited(arguments);
            if (!crossSession) {
                state.emptyTileCount = this.getEmptyTileCount()
            }
            if (this._selectionOrder) { // g2085661: save selectionOrder
                state.selectionOrder = this._selectionOrder;
            }

            // Append grouping information
            if (this._containedGroupId) {
                state.groupId = this._containedGroupId;
            }
            var subContainers = this._getChildContainers();
            if (subContainers.length) {
                state.nestings = [];
                subContainers.forEach(function (container) {
                    var containerState = container.getState(crossSession);
                    containerState.nestingId = that._getProperties(container).childId;
                    container._updateChildStates(containerState, crossSession);
                    state.nestings.push(containerState);
                });
            }
            if (Object.keys(this._lastRegionForGroup).length) {
                state.groupRegions = lang.clone(this._lastRegionForGroup);
            }
            if (crossSession) {
                var t, c, children, order;
                for (t = 0; t < state.tileOccupancy.length; t++) {
                    children = state.tileOccupancy[t].children;
                    for (c = 0; c < children.length; c++) {
                        order = this._selectionOrder[children[c].id];
                        if (order !== undefined) {
                            children[c].selectionOrder = order;
                        }
                    }
                }
                if (this._preMaximizeState) {
                    state.preMaximizeState = this._preMaximizeState;
                }
            }
            this._updateChildStates(state, crossSession);
            return state;
        },

        setState: function (state) {
            var that = this;
            if (!this._started && state !== this._startingState) {
                // Defer state restore until started or first document added, which ever comes first.
                // On reason this necessary is that before this document groups may no have been registered.
                this._startingState = state;
                return;
            }
            this._isInSetState = true;
            if (state.groupRegions) {
                Object.keys(state.groupRegions).forEach(function (groupId) {
                    if (!that._lastRegionForGroup[groupId]) {
                        that._lastRegionForGroup[groupId] = state.groupRegions[groupId];
                    }
                });
            }

            if (state.preMaximizeState) {
                this._preMaximizeState = state.preMaximizeState;
            }
            if (state.selectionOrder) { // g2085661: restore selectionOrder
                this._selectionOrder = state.selectionOrder;
            }

            var removedDocuments = [];
            this._removeDocumentsRecursive(removedDocuments);
            this.inherited(arguments);

            // Create and add sub-container
            var stateSubContainers = state.nestings || state.subContainers;
            if (stateSubContainers) {
                stateSubContainers.forEach(function (subContainerState) {
                    var groupProperties = that.groupRegistry.getProperties(that.groupingFacet, subContainerState.groupId);
                    var groupId = subContainerState.groupId;
                    var subContainer = that._createSubContainer(groupId, groupProperties);
                    var subProperties = that._createSubContainerProperties(subContainer, groupId, groupProperties);
                    subProperties.childId = subContainerState.nestingId || subContainerState.containerId;
                    var tile = that._getTileForChildFromState(state, subProperties.childId);
                    that._addToTabContainer(tile, subContainer, subProperties, undefined, true, true);
                    that._bindChildProperties(subContainer, subProperties);
                    subContainer.setState(subContainerState);
                });
            }
            // Add removed documents back
            this._isAddingBack = true;
            removedDocuments.forEach(function (documentInfo) {
                if (state.closeExtraDocuments && that._getTileForChildFromState(state, documentInfo.properties.childId) < 0) {
                    documentInfo.properties.set(LayoutChildProperty.IS_OPEN, false);
                } else {
                    that.addWithProperties(documentInfo.widget, documentInfo.properties);
                }
            });
            this._isAddingBack = false;

            var t, c, children, order;
            var rootContainer = this._getRootContainer();
            if (state.tileStates && !state.tileOccupancy) {
                // Grandfather the property name "tileStates"
                state.tileOccupancy = state.tileStates;
            }
            if (state.tileOccupancy) {
                for (t = 0; t < state.tileOccupancy.length; t++) {
                    children = state.tileOccupancy[t].children;
                    for (c = 0; c < children.length; c++) {
                        order = children[c].selectionOrder;
                        if (order !== undefined) {
                            this._selectionOrder[children[c].id] = order;
                            if (rootContainer && order > rootContainer._selectionCount)
                                rootContainer._selectionCount = order;
                        }
                    }
                }
            }
            this._isInSetState = false;
        },

        _restoreStartingState: function () {
            this.setState(this._startingState);
            delete this._startingState;
            if (this._endStateRestorePending) {
                this.endStateRestore();
                delete this._endStateRestorePending;
            }
        },

        isRestoringState: function () {
            return this._isInSetState || this.inherited(arguments);
        },

        endStateRestore: function () {
            if (this._startingState) {
                this._endStateRestorePending = true;
                return;
            }
            if (!this._pendingState) {
                return;
            }
            var restoredState = this._pendingState;  // _pendingState cleared by inherited method call below
            this.inherited(arguments);
            this._eliminateEmptySubContainers();
            // After restoring from maximized, eliminate empty tiles only if their number has increased (presumably
            // by virtue of a document being closed while maximized).
            var rootContainer = this._getRootContainer();
            if (!((rootContainer._preMaximizeState && restoredState.emptyTileCount >= this.getEmptyTileCount()) ||
                  this._isSettingTileCoverage || this._isSettingGridSize) &&
                // Also support a layout option specifying that empty tiles be retained
                !(restoredState && restoredState.retainEmptyTiles)) {
                this.eliminateEmptyTiles();
            }

            // For each group having a document present record a showing document as the last selected
            var that = this;
            this.getShowingDocuments().forEach(function (document) {
                var properties = that._getProperties(document);
                var groupProperties = that._getGroupProperties(document, properties);
                if (groupProperties && !groupProperties.getForWindow(window, DocumentGroupProperties.LAST_SELECTED)) {
                    groupProperties.setForWindow(window, DocumentGroupProperties.LAST_SELECTED, document);
                }
            });
        },

        _stateIncludesDocument: function (state, properties, constrainToSubContainer) {
            var childId = properties.childId;

            // g2278453, handle the case where state has the document type of the child being added,
            // and the child is contrained to a sub-container
            if (constrainToSubContainer && state.groupId === properties.setId) {
                return true;
            }

            var that = this;
            var i;
            if (this._getTileForChildFromState(state, childId) >= 0) {
                return true;
            }
            var subContainers = state.nestings || state.subContainers;
            if (subContainers) {
                for (i = 0;i < subContainers.length; i++) {
                    if (that._stateIncludesDocument(subContainers[i], properties, constrainToSubContainer)) {
                        return true;
                    }
                }
            }
            return false;
        },

        /*
         * Updates the child state arrays obtained from each tab container as follows:
         *
         *    1. If capturing state for external (cross-session) purposes, discard entries for children
         *       that will not reopen
         *    2. For children lacking a documentId, update the child Id based on the current title in case the
         * title has been changed.
         */
        _updateChildStates: function (state, crossSession) {
            var that = this;
            var rootContainer = this._getRootContainer();
            var childMap = this._getChildInfoMap();
            state.tileOccupancy.forEach(function (tileState) {
                if (crossSession) {
                    var filteredChildren = [];
                    tileState.children.forEach(function (childState) {
                        var childInfo = childMap[childState.id];
                        var groupProperties = childInfo ? that._getGroupProperties(childInfo.widget, childInfo.properties) : null;
                        if (!groupProperties || groupProperties.willReopen) {
                            filteredChildren.push(childState);
                        }
                    });
                    tileState.children = filteredChildren;
                }
                tileState.children.forEach(function (childState) {
                    var childInfo = childMap[childState.id];
                    // TODO: Eliminate the second clause to support changing document ID
                    if (childInfo && !childInfo.properties.documentId) {
                        childState.id = rootContainer._generateChildId(childInfo.properties);
                    }
                });
            });
        },

        _getChildInfoMap: function () {
            var map = {};
            this.getGrandChildren(function (widget, properties) {
                if (!widget.isInstanceOf(DocumentContainer)) {
                    map[properties.childId] = {widget: widget, properties: properties};
                }
                return false;
            });
            return map;
        },

        // Compose a unique child id by including the grouping id path
        _generateChildId: function (properties) {
            var id = "";
            id = this._appendGroupToId(this.groupingFacet, properties, id);
            id += properties.documentId ? properties.documentId : properties.title;
            return id;
        },

        _appendGroupToId: function (facet, properties, id) {
            if (facet) {
                var groupId = properties[facet];
                id += groupId + "_";
                var groupProperties = this.groupRegistry.getProperties(facet, groupId);
                id = this._appendGroupToId(groupProperties.subGroupingFacet, properties, id);
            }
            return id;
        },

        _removeDocumentsRecursive: function (removedDocuments) {
            var that = this;
            var children = [];
            this._getTabContainers().forEach(function (tabContainer) {
                that._removeChildrenFromTabContainer(tabContainer, children);
            });
            children.forEach(function (childInfo) {
                if (childInfo.widget.isInstanceOf(DocumentContainer)) {
                    childInfo.widget._removeDocumentsRecursive(removedDocuments);
                } else {
                    childInfo.properties[that._getUnbindingKey(childInfo.widget)]();
                    removedDocuments.push(childInfo);
                }
            });
        },

        _areAnyTabContainersRestoringState: function () {
            var result = this.inherited(arguments);
            var i, subContainers;
            if (!result) {
                subContainers = this._getChildContainers();
                for (i = 0; i < subContainers.length && !result; i++) {
                    result = subContainers[i].isRestoringState();
                }
            }
            return result;
        },

        _addChildWatches: function (widget, properties, watches) {
            this.inherited(arguments);
            if (this._widgets[widget.id].isInstanceOf(DocumentContainer)) {
                return;
            }
            var that = this,
                widgetId = widget.id;

            var selectCallback = function (name, oldValue, newValue) {
                var groupId, groupProperties, updatedCount, parentContainer, container;
                if (newValue) {
                    var rootContainer = that._getRootContainer();
                    if (!rootContainer) {
                        // During maximize a sub-container may have been removed from its parent
                        return;
                    }
                    groupProperties = that._getGroupProperties(that._widgets[widgetId], that._properties[widgetId]);
                    if (groupProperties && groupProperties.setForWindow) {
                        groupProperties.setForWindow(window, DocumentGroupProperties.LAST_SELECTED, that._widgets[widgetId]);
                    }
                    updatedCount = rootContainer._selectionCount++;
                    that._selectionOrder[that._properties[widgetId].childId] = updatedCount;
                    container = that;
                    while (container && container._isChildContainer) {
                        parentContainer = container._getParentContainer();
                        groupProperties = parentContainer._getGroupProperties(that._widgets[widgetId], that._properties[widgetId]);
                        if (groupProperties && groupProperties.setForWindow) {
                            groupProperties.setForWindow(window, DocumentGroupProperties.LAST_SELECTED, that._widgets[widgetId]);
                        }
                        parentContainer._selectionOrder[parentContainer._getProperties(container).childId] = updatedCount;
                        container = parentContainer;
                    }

                    groupId = that._getGroupId(that._widgets[widgetId]);
                    if (groupId) {
                        var tab = TabContainerLender.getOwningTabContainer(that._widgets[widgetId]).getTabForChild(that._widgets[widgetId]);
                        if (tab) {
                            domClass.add(tab.domNode, "selectedGroup");
                        }
                    }
                }
            };
            // The above callback should be called before any other "isSelected" callbacks so that when other
            // callbacks are called the group "lastSelected" property is consistent.
            var selectWatch = that._properties[widgetId].preferredWatch ?
                              that._properties[widgetId].preferredWatch(LayoutChildProperty.IS_SELECTED, selectCallback) :
                              that._properties[widgetId].watch(LayoutChildProperty.IS_SELECTED, selectCallback);
            selectWatch.test = "DocumentContainer._addChildWatches.selectWatch";
            watches.push(selectWatch);
            var regionWatch = that._properties[widgetId].watch(LayoutChildProperty.REGION, function (name, oldValue, newValue) {
                var newTile;
                if (!that._isUpdatingRegions && newValue !== oldValue) {
                    if (newValue === "center") {
                        if (that._tileCount === 2) {
                            // Can't move to center when split.  Revert property.
                            setTimeout(function() {
                                var saveUpdatingRegions = that._isUpdatingRegions;
                                that._isUpdatingRegions = true;
                                that._properties[widgetId].set(LayoutChildProperty.REGION, that._getRegionFromTile(that._getTileContainingChild(that._widgets[widgetId])));
                                that._isUpdatingRegions = saveUpdatingRegions;
                            }, 0);
                        }
                    }
                    else if (that._tileCount === 1) {
                        newTile = that._splitTile(0, newValue);
                    } else if (that._tileCount === 2) {
                        newTile = that._getTileFromRegion(newValue);
                    }
                    if (newTile !== undefined) {
                        that._moveChild(that._widgets[widgetId], 1 - newTile, newTile,
                                        that._properties[widgetId].get(LayoutChildProperty.IS_SHOWING) || that.getChildInTile(newTile).isEmpty());
                    }
                }
            });
            watches.push(regionWatch);
            var maximizeWatch = that._properties[widgetId].watch(LayoutChildProperty.IS_MAXIMIZED, function (name, oldValue, newValue) {
                if (!that._maximizeUpdateDepth) {
                    if (newValue) {
                        that._maximize(that._widgets[widgetId]);
                    } else {
                        that._restore(that._widgets[widgetId]);
                    }
                }
                for (var documentId in that._widgets) {
                    that._updateMaximizeButton(that._widgets[documentId]);
                }
            });
            watches.push(maximizeWatch);
        },

        _addGroupWatches: function (groupId, groupProperties) {
            var that = this;
            if (!this._groupWatches[groupId]) {
                this._groupWatches[groupId] = [];
                this._groupWatches[groupId].push(groupProperties.watch(LayoutChildProperty.IS_MAXIMIZED, function (name, oldValue, newValue) {
                    var subContainers = that._getChildContainers(groupId);
                    if (subContainers.length > 0) {
                        if (!that._maximizeUpdateDepth) {
                            if (newValue) {
                                that._maximize(subContainers[0]);
                            } else {
                                that._restore(subContainers[0]);
                            }
                        }
                        that._updateMaximizeButton(subContainers[0]);
                    }
                }));
                this._groupWatches[groupId].push(groupProperties.watch(LayoutChildProperty.TILE, function (name, oldValue, newValue) {
                    if (that._tileUpdateDepth) {
                        return;
                    }
                    if (newValue < 0 || newValue >= that._tileCount) {
                        that._tileUpdateDepth++;
                        groupProperties.set(LayoutChildProperty.TILE, that._getTileContainingGroup(groupId));
                        that._tileUpdateDepth--;
                    } else {
                        that._moveGroup(groupId, newValue);
                    }
                }));
                this._groupWatches[groupId].push(groupProperties.watch(DocumentGroupProperties.SUB_GRID_SIZE, function (name, oldValue, newValue) {
                    if (newValue && !that._isUpdatingSubContainerGridSize) {  // Will be undefined when sub-container removed.
                        that._subTileGroup(groupId, newValue);
                    }
                }));
                this._groupWatches[groupId].push(groupProperties.watch(DocumentGroupProperties.IS_OPEN, function (name, oldValue, newValue) {
                    if (newValue && groupProperties.constrainToSubContainer && !groupProperties.documentCount && !that.hasChildContainer(groupId)) {
                        // Add a subcontainer for the group
                        var tile = groupProperties.tile;
                        if (tile === undefined) {
                            tile = that._chooseTile();
                        }
                        if (tile === undefined) {
                            tile = 0;
                        }
                        that._addSubContainer(groupId, groupProperties, tile);
                    } else if (!newValue) {
                        that.closeGroup(groupId).then(
                            function() {
                                that._getChildContainers(groupId).forEach(function (subContainer) {
                                    that._eliminateSubContainer(subContainer);
                                });
                            },
                            function() {
                                // Close was vetoed, reset the IS_OPEN property
                                setTimeout(function() {
                                    groupProperties.set(DocumentGroupProperties.IS_OPEN, true);
                                }, 0);
                            }
                        );
                    }
                }));
            }
        },

        _removeGroupWatches: function (groupId) {
            var watches = this._groupWatches[groupId];
            if (watches) {
                watches.forEach(function (watch) {
                    watch.remove();
                });
                delete this._groupWatches[groupId];
            }
        },

        /**
         * @deprecated set gridSize instead
         * Sets the container arrangement automatically redistributing documents as appropriate.
         *
         * @param arrangement DocumentContainer.NO_SPLIT, DocumentContainer.LEFT_RIGHT_SPLIT or DocumentContainer.TOP_BOTTOM_SPLIT.
         * @private
         * @instance
         */
        _setArrangementAttr: function (arrangement) {
            if (arrangement === this.get("arrangement")) {
                return;
            }
            switch (arrangement) {
                case DocumentContainer.NO_SPLIT:
                    this.set(TileContainer.GRID_SIZE, {w: 1, h: 1});
                    break;
                case DocumentContainer.LEFT_RIGHT_SPLIT:
                    this.set(TileContainer.GRID_SIZE, {w: 2, h: 1});
                    break;
                case DocumentContainer.TOP_BOTTOM_SPLIT:
                    this.set(TileContainer.GRID_SIZE, {w: 1, h: 2});
                    break;
            }
        },

        /**
         * @deprecated get gridSize instead
         *
         * @returns {number} the current container arrangement
         * @private
         * @instance
         */
        _getArrangementAttr: function () {
            return this._getArrangementFromGridSize(this.gridSize);
        },

        _getArrangementFromGridSize: function (gridSize) {
            var arrangement = DocumentContainer.CUSTOM_TILING;
            var tileCount = gridSize.w * gridSize.h;
            if (tileCount === 1) {
                arrangement = DocumentContainer.NO_SPLIT;
            } else if (tileCount === 2) {
                arrangement = gridSize.w === 2 ? DocumentContainer.LEFT_RIGHT_SPLIT : DocumentContainer.TOP_BOTTOM_SPLIT;
            }
            return arrangement;
        },

        /**
         * Sets the position of the tabs.  The setting is applied to all tab container within the document container.
         *
         * @param position "top", "bottom", "left", "right'
         * @private
         * @instance
         */
        _setTabPositionAttr: function (position) {
            this._setTabContainerProperty(TabContainer.TAB_POSITION, this.tabPosition, position);
        },

        /**
         * @returns {string} the position of the document tabs.  Possible return values are "top", "bottom", "left", "right"
         * and "mixed".  The latter applies when there are multiple tab containers having different positions.
         * @private
         * @instance
         */
        _getTabPositionAttr: function () {
            return this._getTabContainerProperty(TabContainer.TAB_POSITION, "top");
        },

        _setTabContainerProperty: function (name, oldValue, newValue) {
            this._getTabContainers().forEach(function (tabContainer) {
                tabContainer.set(name, newValue);
            });
            this._getChildContainers().forEach(function (subContainer) {
                subContainer.set(name, newValue);
            });
            this.tabProperties[name] = newValue;
            this.notifyStateWatchers(TabContainer.TAB_POSITION, oldValue, this.get(TabContainer.TAB_POSITION));
            if (this.get(name) !== oldValue) {
                this.emit(LayoutContainerEvent.STATE_CHANGED);
            }
        },

        _getTabContainerProperty: function (name, defaultValue) {
            var tabContainers = this._getTabContainers();
            var value = tabContainers.length === 0 ? defaultValue : tabContainers[0].get(name);
            var i;
            for (i = 1; i < tabContainers.length; i++) {
                if (tabContainers[i].get(name) !== value) {
                    value = "mixed";
                    break;
                }
            }
            var subContainers = this._getChildContainers();
            for (i = 0; i < subContainers.length; i++) {
                if (subContainers[i].get(name) !== value) {
                    value = "mixed";
                    break;
                }
            }
            return value;
        },

        _setPlaceHolderTextAttr: function(text) {
            this.placeHolderText = text;
            if (this.domNode) {
                if (text) {
                    if (!this._placeHolder) {
                        this._addPlaceHolder();
                    }
                    this._setPlaceHolderText(text);
                } else if (this._placeHolder) {
                    this._removePlaceHolder();
                }
            }
        },

        _addPlaceHolder: function() {
            this._placeHolder = domConstruct.create("div", {id : this.id + "_placeHolder"}, this.domNode);
            domClass.add(this.domNode, "placeHolderContainer");
            domClass.add(this._placeHolder, "placeHolder");
        },

        _removePlaceHolder: function() {
            if (this._placeHolder) {
                domConstruct.destroy(this._placeHolder);
                delete this._placeHolder;
            }
        },

        _setPlaceHolderText: function(text) {
            if (this._placeHolder) {
                this._placeHolder.textContent = text;
            }
        },

        _createTabContainer: function () {
            var that = this;
            // Specify some additional tab container properties before creating the tab container
            this.tabProperties.tabAnimationEnabled = this.animationEnabled;
            this.tabProperties.displayToolTipsOnTruncatedTabs = true;
            if (this.groupingFacet && this.groupRegistry) {
                this.tabProperties.actionsMenuCreator = lang.hitch(this, "_createSetActionsMenu");
                this.tabProperties.groupPropertiesFetcher = lang.hitch(this, "_getGroupProperties");
            }
            if (this._isChildContainer) {
                this.tabProperties.isContentBorderVisible = true;
            }
            var container = this.inherited(arguments);
            // Prevent default context menu on empty container
            LayoutUtils.preventDefaultContextMenu(container.containerNode, false);

            var maximizeButton;
            if (this.offerMaximizeButton === true || (this.offerMaximizeButton === undefined && container.hideSingleTab)) {
                maximizeButton = domConstruct.create("div", {id : container.id + "_maximizeButton"});
                domClass.add(maximizeButton, "maximizeButton");
                domAttr.set(maximizeButton, "title", layoutL10n.maximize);
                dom.setSelectable(maximizeButton, false);
                LayoutUtils.preventFocus(maximizeButton);
                on(maximizeButton, "click", function () {
                    var showingChild = container.get(TabContainer.SHOWING_CHILD);
                    if (showingChild) {
                        that._toggleMaximize(showingChild);
                    }
                });
                container.setUpperRightOverlay(maximizeButton, function(tabShowing) {
                    var showingChild = container.get(TabContainer.SHOWING_CHILD);
                    return showingChild && (that.offerMaximizeButton
                        || (that.offerMaximizeButton === undefined && !tabShowing &&
                            (MaximizingContainer.canMaximizeGlobally(showingChild) ||
                            (MaximizingContainer.canRestoreGlobally(showingChild)))));
                });
            }

            this._updatePlusActions(container);
            if (!this._containedGroupId) {
                container._actionsButton = this._createActionsButton(container, 1);
                this._updateActionsButtonLocation(container);
                var gripActionsButton = this._createActionsButton(container, 2);
                container.setGripNeighbor(gripActionsButton);
                this._createActionsMenu(container, [container._actionsButton, gripActionsButton]);
            }
            container.own(container.watch(TabContainer.SHOWING_CHILD, function (name, oldChild, newChild) {
                if (oldChild) {
                    var oldProperties = that._getProperties(oldChild);
                    if (oldProperties.get(LayoutChildProperty.IS_MAXIMIZED)) {
                        that._maximizeUpdateDepth++;
                        oldProperties.set(LayoutChildProperty.IS_MAXIMIZED, false);
                        if (newChild) {
                            that._getProperties(newChild).set(LayoutChildProperty.IS_MAXIMIZED, true);
                        }
                        that._maximizeUpdateDepth--;
                    }
                }
                that._updatePlusActions(container);
            }));

            container.own(container.watch(TabContainer.TAB_POSITION, function (name, oldValue) {
                that.notifyStateWatchers(TabContainer.TAB_POSITION, oldValue, that.get(TabContainer.TAB_POSITION));
                that._updateActionsButtonLocation(container);
            }));
            container.own(on(container.tablist.domNode, "dblclick", lang.hitch(this, "_handleTabDoubleClick", container)));
            this._createTabContextMenu(container);
            return container;
        },

        _handleTabDoubleClick: function (tabContainer, e) {
            var tab = this._getEnclosingTab(tabContainer, e.target);
            // Fix for g2109649: only maximize if tab.widget is not destroyed yet
            if (tab && tab.widget && tab.widget.domNode) {
                this._toggleMaximize(tab.widget);
            }
        },

        _toggleMaximize: function (widget) {
            if (MaximizingContainer.canMaximizeGlobally(widget)) {
                this._maximize(widget);
            } else if (MaximizingContainer.canRestoreGlobally(widget)) {
                this._restore(widget);
            }
        },

        _createActionsButton: function (container, count) {
            var actionsButton = document.createElement("div");
            actionsButton.setAttribute("id", container.id + "ActionsButton" + count);
            domClass.add(actionsButton, "actionsButton");

            var actionsButtonWrapper = document.createElement("div");
            actionsButtonWrapper.setAttribute("id", container.id + "ActionsButtonWrapper" + count);
            actionsButtonWrapper.setAttribute("role", "button");
            actionsButtonWrapper.setAttribute("aria-haspopup", "true");
            actionsButtonWrapper.setAttribute("title", lang.replace(layoutL10n.childActions, {placeholder: layoutL10n.document}));

            actionsButtonWrapper.appendChild(actionsButton);
            domClass.add(actionsButtonWrapper, "actionsButtonWrapper");

            dom.setSelectable(actionsButtonWrapper, false);
            LayoutUtils.preventFocus(actionsButtonWrapper, true);

            actionsButtonWrapper.addEventListener("focus", function() {
                actionsButtonWrapper.classList.add("actionsButtonFocused");
            });

            actionsButtonWrapper.addEventListener("blur", function() {
                actionsButtonWrapper.classList.remove("actionsButtonFocused");
            });

            actionsButtonWrapper.tabIndex = -1;
            return actionsButtonWrapper;
        },

        _updateActionsButtonLocation: function (container) {
            if (container.tabPosition === "top" || container.tabPosition === "bottom") {
                container._tabBar.setPostNeighbor(container._actionsButton);
            } else if (container._tabBar.getPostNeighbor() === container._actionsButton) {
                container._tabBar.setPostNeighbor(null);
            }
        },

        _createTabContextMenu: function (container) {
            var that = this;
            var tabsNode = container.tablist.domNode;
            var menu = new ContextMenu({
                id: tabsNode.id + "Menu",
                targetNodes: [tabsNode, "#" + tabsNode.id + " *"]
            });
            container.own(menu);
            menu.willOpenCallback = function (event) {
                var tab = that._getEnclosingTab(container, event.target);
                var context;
                if (tab) {
                    context = that._getContextFromWidget(tab.widget);
                    // context.isTab = true;
                    // Not currently needed but could be used to differentiate tab context menu from action drop down
                } else {
                    var widget = container.get(TabContainer.SHOWING_CHILD);
                    context = that._getContextFromWidget(widget);
                }
                that._populateMenu(container, menu, context);
            };
        },

        _createActionsMenu: function (container, buttons) {
            var that = this;
            var menu = new ContextMenu({
                id: container.id + "ActionsMenu",
                targetNodes: buttons
            });
            container.own(menu);
            buttons.forEach(function (button) {
                on(button, touch.press, function (e) {
                    // Open menu if primary press and not already open
                    var menuIsClosed = (!menu.domNode.parentElement || domStyle.get(menu.domNode.parentElement, "display") === "none");
                    var isPrimaryPress = mouse.isLeft(e) || (e.type === "touchstart");
                    if (isPrimaryPress && menuIsClosed) {
                        event.stop(e);
                        menu.open({target: e.currentTarget});
                        var actionNodeWrapper = e.currentTarget;
                        actionNodeWrapper.setAttribute("aria-expanded", "true");
                        menu.own(menu.on("close", function () {
                            actionNodeWrapper.removeAttribute("aria-expanded");
                        }));
                    }
                });
            });
            menu.willOpenCallback = function () {
                var widget = container.get(TabContainer.SHOWING_CHILD);
                that._populateMenu(container, menu, that._getContextFromWidget(widget));
            };
            return menu;
        },

        _createSetActionsMenu: function (container, setIdOrSubContainer, isContextMenu) {
            var that = this;
            var context, groupId;
            if (typeof setIdOrSubContainer === "string") {
                groupId = setIdOrSubContainer;
                context = this._getContextForSet(container, setIdOrSubContainer);
            } else {
                groupId = this._getProperties(setIdOrSubContainer).childId;
                context = this._getContextFromWidget(setIdOrSubContainer);
            }
            var menu = new ContextMenu({
                id: groupId + (isContextMenu ? "ContextMenu" : "ActionsMenu"),
                targetNodes: []  // To be populated by TabGroupHeader
            });
            menu.willOpenCallback = function () {
                that._populateMenu(container, menu, context);
            };
            return menu;
        },

        _populateMenu: function (container, menu, context) {
            var document, properties, groupId, groupProperties, maximizeItem;
            var that = this;
            // Clear previous items from the menu.  One might expect this to be done on menu close but doing so
            // leads to problems if a menu is closed by a right click gesture that reopens it.
            this._clearMenu(menu);
            this._isPopulatingMenu = true;
            // Add document specific items (maximize, undock and close)
            if (context.isDocument) {
                document = context.children[0];
                properties = this._getProperties(document);
                if (this.groupingFacet) {
                    groupId = properties[this.groupingFacet];
                    if (groupId) {
                        groupProperties = this.groupRegistry.getProperties(this.groupingFacet, groupId);
                    }
                }
                if (properties.actions) {
                    ActionsMenu._addMenuItems(menu, properties.actions);
                    menu.addChild(new MenuSeparator());
                }

                maximizeItem = this._createMaximizeDocumentMenuItem(document, properties);
                if (maximizeItem) {
                    menu.addChild(maximizeItem);
                }
                if (this._getUndockFunction(properties)) {
                    menu.addChild(this._createUndockDocumentMenuItem(document, properties));
                }
                if (groupId && this._getUndockFunction(groupProperties)) {
                    menu.addChild(this._createUndockGroupMenuItem(groupId, groupProperties))
                }
                if (this._isClosable(document)) {
                    menu.addChild(this._createCloseDocumentMenuItem(document, properties));

                    // Add "Close <group>" but not if there is only one group
                    if (groupId && this._getRegisteredGroupCount() > 1 && this._isAnyInGroupClosable(groupId)) {
                        menu.addChild(this._createCloseGroupMenuItem(groupId, groupProperties));
                    }
                }
            }
            if (context.subContainer || context.children.length > 1) {
                groupId = context.subContainer ? context.subContainer._containedGroupId :
                                                 this._getGroupId(context.children[0]);
                groupProperties = this.groupRegistry.getProperties(this.groupingFacet, groupId);
                if (context.subContainer) {
                    maximizeItem = this._createMaximizeSetMenuItem(groupProperties, context.subContainer);
                    if (maximizeItem) {
                        menu.addChild(maximizeItem);
                    }
                }
                if (this._getUndockFunction(groupProperties)) {
                    menu.addChild(this._createUndockSetMenuItem(groupProperties, groupId, context.children))
                }
                if (this._isAnyClosable(context.children) || context.children.length === 0) {
                    menu.addChild(this._createCloseSetMenuItem(groupProperties, context.children));
                }
            }
            // Offer "Close All" only if there is a single document group.  Otherwise, Close <group> will have been
            // added above.
            if (this._getRegisteredGroupCount() <= 1 && this._hasClosableDocument()) {
                menu.addChild(this._createCloseAllMenuItem());
            }
            if (menu.getChildren().length > 0) {
                menu.addChild(new MenuSeparator());
            }
            if (this.userTilingEnabled) {
                if (this._getRegisteredGroupCount() > 1) {
                    // Offer a sub-tile menu to tile members of the document's group
                    if (context.isDocument && !this.restrictToSingleSplit) {
                        var child = context.children[0];
                        var groupId = this._getGroupId(child);
                        if (groupId) {
                            groupProperties = this.groupRegistry.getProperties(this.groupingFacet, groupId);
                            var tabContainer = TabContainerLender.getOwningTabContainer(child);
                            var children = [];
                            tabContainer.getChildrenInTabOrder().forEach(function (otherChild) {
                                if (that._getGroupId(otherChild) === groupId) {
                                    children.push(otherChild);
                                }
                            });
                            if (children.length > 1) {
                                menu.addChild(this._createTileSetSubMenu(menu, children, undefined, groupProperties, this.getTileContaining(tabContainer)));
                            }
                        }
                    } else if (context.subContainer || (context.children.length > 1 && !this.restrictToSingleSplit)) {
                        menu.addChild(this._createTileSetSubMenu(menu, context.children, context.subContainer, groupProperties, this.getTileContaining(container)));
                    }
                }
                menu.addChild(this._createTileAllSubMenu(menu));
            }
            menu.addChild(this._createTabPositionSubMenu(container));
            this._isPopulatingMenu = false;
        },

        _createMaximizeDocumentMenuItem: function (document, properties) {
            return this._createMaximizeMenuItem(document, properties.title);
        },

        _createUndockDocumentMenuItem: function (document, properties) {
            var that = this;
            var item = new MenuItem({
                text: lang.replace(layoutL10n.undockSomething, {placeholder: properties.title}),
                tag: "undockDocument"
            });
            on(item.domNode, "click",function () {
                that._getUndockFunction(properties)(document);
            });
            return item;
        },

        _createCloseDocumentMenuItem: function (document, properties) {
            var that = this;
            var item = new MenuItem({
                text: lang.replace(layoutL10n.closeSomething, {placeholder: properties.title}),
                tag: "closeDocument"
            });
            on(item.domNode, "click",function () {
                that.closeDocument(document);
            });
            return item;
        },

        _createUndockGroupMenuItem: function (groupId, groupProperties) {
            var that = this;
            var item = new MenuItem({
                text: lang.replace(layoutL10n.undockSomething, {placeholder: groupProperties.collectiveLabel}),
                tag: "undockGroup"
            });
            on(item.domNode, "click",function () {
                that._getUndockFunction(groupProperties)();
            });
            return item;
        },

        _createCloseGroupMenuItem: function (groupId, groupProperties) {
            var that = this;
            var item = new MenuItem({
                text: lang.replace(layoutL10n.closeSomething, {placeholder: groupProperties.collectiveLabel}),
                tag: "closeAll"
            });
            on(item.domNode, "click",function () {
                that.closeGroup(groupId);
            });
            return item;
        },

        _createMaximizeSetMenuItem: function (groupProperties, subContainer) {
            return this._createMaximizeMenuItem(subContainer, groupProperties.collectiveLabel);
        },

        _createMaximizeMenuItem: function (child, title) {
            var that = this;
            var item;
            if (MaximizingContainer.canMaximizeGlobally(child)) {
                item = new MenuItem({
                    text: layoutL10n.maximize,
                    tag: "maximize" + (!!child._isChildContainer ? "Group" : "Document")
                });
                on(item.domNode, "click",function () {
                    that._maximize(child);
                });
            } else if (MaximizingContainer.canRestoreGlobally(child)) {
                item = new MenuItem({
                    text: layoutL10n.restore,
                    tag: "restore" + (!!child._isChildContainer ? "Group" : "Document")
                });
                on(item.domNode, "click",function () {
                    that._restore(child);
                });
            }
            return item;
        },

        _createUndockSetMenuItem: function (groupProperties, groupId, children) {
            var that = this;
            var item = new MenuItem({
                text: lang.replace(layoutL10n.undockSomething, {placeholder: groupProperties.collectiveLabel}),
                tag: "undockGroup"
            });
            on(item.domNode, "click",function () {
                that._getUndockFunction(groupProperties)(children);
            });
            return item;
        },

        _createCloseSetMenuItem: function (groupProperties, children) {
            var that = this;
            var item = new MenuItem({
                text: lang.replace(layoutL10n.closeSomething, {placeholder: groupProperties.collectiveLabel}),
                tag: "closeAll"
            });
            on(item.domNode, "click",function () {
                if (children.length === 0) {
                    var groupId = groupProperties[that.groupingFacet];
                    that._getChildContainers(groupId).forEach(function (subContainer) {
                        that._eliminateSubContainer(subContainer);
                    });
                    if (groupProperties.documentCount === 0) {
                        groupProperties.set(DocumentGroupProperties.IS_OPEN, false);
                    }
                } else {
                    that._closeDocuments(children);
                }
            });
            return item;
        },

        _createTileSetSubMenu: function (menu, children, subContainer, groupProperties, tile) {
            var that = this;
            var subMenu = this._createTileSubMenu(menu, subContainer, children.length, !!subContainer,
                                                  groupProperties.collectiveLabel, groupProperties.defaultTiling,
                                                  function (gridData) {
                if (gridData.columns === 0 || gridData.rows === 0) {
                    return;
                }
                var gridSize = {w: gridData.columns, h: gridData.rows};
                if (subContainer) {
                    // Group is already in a sub-container
                    if (that._isSingleGrid(gridSize) && !groupProperties.defaultTiling && !groupProperties.constrainToSubContainer) {
                        that._eliminateSubContainer(subContainer);
                    } else {
                        // Update the sub-container's tiling
                        subContainer.set(TileContainer.GRID_SIZE, gridSize);
                    }
                } else if (!that._isSingleGrid(gridSize)) { // g2149206: Only extract if we are not asked to create a 1x1 grid
                    that._extractToSubContainer(children, tile, gridSize);
                }
            });
            return subMenu;
        },
        
        _isSingleGrid: function (gridSize) {
            return ((gridSize.h || gridSize.rows) === 1) && ((gridSize.w || gridSize.columns) === 1);
        },

        _createTileAllSubMenu: function (menu) {
            var that = this;
            var subMenu = this._createTileSubMenu(menu, this, this._getChildCount(), true, undefined, undefined, function (gridData) {
                if (gridData.columns === 0 || gridData.rows === 0) {
                    return;
                }
                that.set(TileContainer.GRID_SIZE, {w: gridData.columns, h: gridData.rows});
            });
            return subMenu;
        },

        _createTileSubMenu: function (menu, tileContainer, documentCount, includeSingle, targetLabel, defaultTiling, callback) {
            var gridSize = tileContainer ? tileContainer.get(TileContainer.GRID_SIZE) : {w: 1, h: 1};
            var subMenu = new Menu();
            var item;
            if (includeSingle) {
                item = new RadioMenuItem({
                    text: layoutL10n.singleTileLabel,
                    checked: gridSize.w === 1 && gridSize.h === 1,
                    closeMenuOnClick: true,
                    tag: "Single"
                });
                on(item.domNode, "click", function () {
                    callback({rows: 1, columns: 1});
                });
                subMenu.addChild(item);
            }
            item = new RadioMenuItem({
                text: layoutL10n.leftRightSplitLabel,
                checked: gridSize.w === 2 && gridSize.h === 1,
                closeMenuOnClick: true,
                tag: "LeftRight"
            });
            on(item.domNode, "click", function () {
                callback({rows: 1, columns: 2});
            });
            subMenu.addChild(item);
            item = new RadioMenuItem({
                text: layoutL10n.topBottomSplitLabel,
                checked: gridSize.w === 1 && gridSize.h === 2,
                closeMenuOnClick: true,
                tag: "TopBottom"
            });
            on(item.domNode, "click",function () {
                callback({rows: 2, columns: 1});
            });
            subMenu.addChild(item);

            if (defaultTiling && tileContainer) {
                item = new RadioMenuItem({
                    text: "Default Tiling",  // TODO: Localize
                    checked: TileTabContainer.areStatesSimilar(defaultTiling, tileContainer.getState()),
                    closeMenuOnClick: true,
                    tag: "Default"
                });
                on(item.domNode, "click", function () {
                    tileContainer.setState(defaultTiling);
                });
                subMenu.addChild(item);
            }
            if (!this.restrictToSingleSplit) {
                var gridPicker = new GridPicker({
                    occupancy: documentCount
                });
                gridPicker.on(GridPicker.SELECTION_CHANGED, function (gridData) {
                    menu.close();
                    callback(gridData);
                });
                subMenu.addChild(new MenuSeparator());
                subMenu.addChild(gridPicker);
            }
            return new PopupMenuItem({
                text: !tileContainer || tileContainer._isChildContainer
                      ? (targetLabel ? lang.replace(layoutL10n.subtileSomething, {placeholder: targetLabel}) : layoutL10n.subtile)
                      : layoutL10n.tileAll,
                menu: subMenu,
                tag: !tileContainer || tileContainer._isChildContainer ? "tileGroup" : "tileAll"
            });
        },

        _createCloseAllMenuItem: function () {
            var that = this;
            var item = new MenuItem({
                text: layoutL10n.closeAll,
                tag: "closeAll"
            });
            on(item.domNode, "click", function() {
                that.closeAll();
            });
            return item;
        },

        _createTabPositionSubMenu: function (container) {
            var that = this;
            var subMenu = new Menu();
            var tabPositionItems = {};
            ["top", "bottom", "left", "right"].forEach(function (position) {
                tabPositionItems[position] =  new RadioMenuItem({
                    text: layoutL10n[position],
                    closeMenuOnClick: true,
                    checked: position === container.tabPosition,
                    buttonGroup: "tabArrangementButtonGroup",
                    tag: "TabPosition" + position.substr(0,1).toUpperCase() + position.substr(1)
                });
                on(tabPositionItems[position].domNode, "change", function() {
                    var sharedPosition, savePosition;
                    container.set(TabContainer.TAB_POSITION, position);
                    sharedPosition = that.get(TabContainer.TAB_POSITION);
                    if (sharedPosition !== that.tabPosition) {
                        savePosition = that.tabPosition;
                        that._set(TabContainer.TAB_POSITION, sharedPosition);  // Notifies
                        if (sharedPosition === "mixed") {
                            // Don't actually keep "mixed" as the value
                            that.tabPosition = savePosition;
                        }
                        that.emit(LayoutContainerEvent.STATE_CHANGED);
                    }
                });
                subMenu.addChild(tabPositionItems[position]);
            });
            var item = new PopupMenuItem({
                text: layoutL10n.tabPosition,
                menu: subMenu,
                tag: "tabPosition"
            });
            return item;
        },

        _clearMenu: function (menu) {
            menu.getChildren().forEach(function(child) {
                child.destroyRecursive();
            });
        },

        _getContextFromWidget: function (widget) {
            var context = {};
            if (widget && widget.isInstanceOf(DocumentContainer)) {
                context.children = widget.getGrandChildren();
                context.subContainer = widget;
            } else if (widget) {
                context.children = [widget];
                context.isDocument = true;
            } else {
                context.children = [];
            }
            return context;
        },

        _getContextForSet: function (tabContainer, groupId) {
            var that = this;
            var context = {
                children: []
            };
            tabContainer.getChildrenInTabOrder().forEach(function (child) {
                if (that._getGroupId(child) === groupId) {
                    context.children.push(child);
                }
            });
            return context;
        },

        _getEnclosingTab: function(container, node) {
            while (node) {
                if (domClass.contains(node, "tab")) {
                    return registry.byNode(node);
                }
                node = node.parentNode;
            }
            return null;
        },

        _closeDocuments: function (documents, force) {
            var that = this;
            var approvers = [];
            var queried = [];
            var nonQueried = [];
            var deferred = new Deferred();
            documents.forEach(function (document) {
                var properties = that._getProperties(document);
                if (properties && properties.closable && properties.canClose && !force) {
                    queried.push(document);
                    approvers.push(lang.hitch(properties, "canClose"));
                } else {
                    nonQueried.push(document);
                }
            });
            allApprove(approvers).then(function () {
                queried.forEach(function (document) {
                   that.closeDocument(document, true);
                });
                nonQueried.forEach(function (document) {
                    if (force || that._isClosable(document)) {
                        that.closeDocument(document, force);
                    }
                });
                deferred.resolve();
            }, function () {deferred.reject();});
            return deferred;
        },

        _isClosable: function (document) {
            return this._getProperties(document).closable !== false;
        },

        _isAnyInGroupClosable: function (groupId) {
            return this._isAnyClosable(this.getDocuments(groupId));
        },

        _hasClosableDocument: function () {
            return this._isAnyClosable(this.getDocuments());
        },

        _isAnyClosable: function (documents) {
            var i;
            for (i = 0; i < documents.length; i++) {
                if (this._isClosable(documents[i])) {
                    return true;
                }
            }
            return false;
        },

        _gatherDocuments: function (documents, predicate) {
            this.getGrandChildren().forEach(function (child) {
                if (child.isInstanceOf(DocumentContainer)) {
                    child._gatherDocuments(documents, predicate);
                } else if (predicate(child)) {
                    documents.push(child);
                }
            });
        },

        _maximize: function (widget) {
            this._isMaximizing = true;
            MaximizingContainer.maximizeGlobally(widget);
            this._maximizeUpdateDepth++;
            this._getProperties(widget).set(LayoutChildProperty.IS_MAXIMIZED, true);
            this._maximizeUpdateDepth--;
            this._isMaximizing = false;
        },

        _restore: function (widget) {
            // If widget is subContainer it will be replaced upon restore.  Therefore need to get properties first.
            this._isRestoringFromMaximize = true;
            var properties = this._getProperties(widget);
            MaximizingContainer.restoreGlobally(widget);
            this._maximizeUpdateDepth++;
            properties.set(LayoutChildProperty.IS_MAXIMIZED, false);
            this._maximizeUpdateDepth--;
            this._isRestoringFromMaximize = false;
        },

        ancestorMaximizeUpdated: function (isMaximized) {
            var showingChild, properties;
            if (isMaximized) {
                if (this._tileCount === 1) {
                    showingChild = this.getChildInTile(0).get(TabContainer.SHOWING_CHILD);
                    if (showingChild) {
                        properties = this._getProperties(showingChild);
                        if (showingChild.isInstanceOf(DocumentContainer)) {
                            properties = properties.groupProperties;
                        }
                        this._maximizeUpdateDepth++;
                        properties.set(LayoutChildProperty.IS_MAXIMIZED, true);
                        this._maximizeUpdateDepth--;
                    }
                }
            } else if (!this._isRestoringFromMaximize) {
                // Clear the pre-maximized state unless the container is already participating in the restore
                // (see geck 1770380).
                this._clearPreMaximizeState();
            }
        },

        _clearPreMaximizeState: function () {
            delete this._preMaximizeState;
            this._maximizeUpdateDepth++;
            this.getGrandChildren(function (widget, properties) {
                if (widget.isInstanceOf(DocumentContainer)) {
                    properties = properties.groupProperties;
                }
                properties.set(LayoutChildProperty.IS_MAXIMIZED, false);
                return false;
            });
            this._maximizeUpdateDepth--;
        },

        _getParentContainer: function () {
            var parent = this.getParent();
            if (this._isChildContainer && parent && parent.isInstanceOf(TabContainer)) {
                parent = parent.getParent();
                if (parent && parent.isInstanceOf(DocumentContainer)) {
                    return parent;
                }
            }
            return null;
        },

        _getRootContainer: function () {
            var container = this;
            while (container && container._isChildContainer) {
                container = container._getParentContainer();
            }
            return container;
        },

        _getChildContainers: function (groupId) {
            return this.getGrandChildren(function(child) {
                return child.isInstanceOf(DocumentContainer) &&
                       (!groupId || child._containedGroupId === groupId);
            });
        },

        hasChildContainer: function (groupId) {
            return this._getChildContainers(groupId).length > 0;
        },

        _getChildCount: function () {
            return this.getGrandChildren().length;
        },

        _getGroupCount: function () {
            return Object.keys(this._getDocumentCensus()).length;
        },

        _getDocumentCensus: function () {
            var that = this;
            var census = {};
            this.getGrandChildren().forEach (function (child) {
                if (child.isInstanceOf(DocumentContainer)) {
                    census[child._containedGroupId] = child.getDocumentCount();
                } else {
                    var properties = that._getProperties(child);
                    var groupId;
                    if (that.groupingFacet) {
                        groupId = properties[that.groupingFacet];
                    }
                    if (census[groupId]) {
                        census[groupId]++;
                    } else {
                        census[groupId] = 1;
                    }
                }
            });
            return census;
        },

        _getGroupId: function (widget, properties) {
            if (!properties) {
                properties = this._getProperties(widget);
            }
            return widget.isInstanceOf(DocumentContainer) ? widget._containedGroupId :
                   (this.groupingFacet && properties ? properties[this.groupingFacet] : "");
        },

        _getGroupProperties: function (widget, properties) {
            var groupId = this._getGroupId(widget, properties);
            return groupId ? this.groupRegistry.getProperties(this.groupingFacet, groupId) : null;
        },

        _getRegisteredGroupCount: function () {
            return this.groupingFacet ? this.groupRegistry.getIds(this.groupingFacet).length : 0;
        },

        _getUndockFunction: function (properties) {
            return properties.getForWindow && properties.getForWindow(window, "_undock");
        },

        _getTileContainingGroup: function (groupId) {
            var that = this;
            var groupTile = -1;
            var tile, tabContainer;
            for (tile = 0; tile < this._tileCount; tile++) {
                tabContainer = this.getChildInTile(tile);
                if (tabContainer && !tabContainer.isEmpty()) {
                    tabContainer.getChildrenInTabOrder().forEach(function (child) {
                        if (that._getGroupId(child) == groupId) {
                            if (groupTile === -1) {
                                groupTile = tile;
                            } else if (groupTile != tile) {
                                groupTile = -2;
                            }
                        }
                    });
                }
            }
            if (groupTile === -2) {
                groupTile = -1;
            }
            return groupTile;
        },

        _getMostRecent: function (groupId, showingOnly) {
            var that = this;
            var mostRecent = {
                tile: -1
            };
            var recentOrder = -1;
            var tabContainers = this._getTabContainers();
            var tile, properties, order;
            for (tile = 0; tile < tabContainers.length; tile++) {
                tabContainers[tile].getChildrenInTabOrder().forEach(function (child) {
                    properties = that._properties[child.id];
                    order = that._selectionOrder[properties.childId];
                    if (order === undefined) {
                        order = 0;
                    }
                    if (order > recentOrder && (!showingOnly || properties.get(LayoutChildProperty.IS_SHOWING)) &&
                        (!groupId || properties.groupId === groupId ||
                        (that.groupingFacet && properties[that.groupingFacet] === groupId)))
                    {
                        recentOrder = order;
                        mostRecent.tile = tile;
                        mostRecent.child = child;
                    }
                });
            }
            return mostRecent;
        },

        _addToTabContainer: function (tile, widget, properties) {
            this.inherited(arguments);
            var region = this._getRegionFromTile(tile);
            if (region) {
                this._isUpdatingRegions = true;
                properties.set(LayoutChildProperty.REGION, region);
                if (this.groupingFacet) {
                    this._updateGroupRegion(properties[this.groupingFacet]);
                }
                this._isUpdatingRegions = false;
            }
        },

        _handleChildRemove: function (tile, widget, properties, wasSelected) {
            var groupProperties, groupId, count, nextDocument;
            var that = this;
            if (!this._isTemporaryRemove) {
                delete this._selectionOrder[properties.childId];
            }
            this.inherited(arguments);
            if (!this._isTemporaryRemove) {
                if (!properties.get("isUndocking") && !properties.get("isDocking")) {
                    properties.set(LayoutChildProperty.IS_OPEN, false);
                    delete properties.index;
                }
                groupProperties = this._getGroupProperties(widget, properties);
                if (groupProperties && groupProperties.getForWindow && groupProperties.setForWindow) {
                    if (groupProperties.getForWindow(window, DocumentGroupProperties.LAST_SELECTED) === widget) {
                        // Wait for potential next document selection
                        setTimeout(function() {
                            if (groupProperties.getForWindow(window, DocumentGroupProperties.LAST_SELECTED) === widget) {
                                // The removed child is still the last selected.  Replace it with the most recently
                                // selected group member that is showing
                                groupProperties.setForWindow(window, DocumentGroupProperties.LAST_SELECTED,
                                                             that._getMostRecent(that._getGroupId(widget, properties), true).child);
                            }
                        }, 0);
                    }
                }
                if (!this._destroyed && !this._beingDestroyed) {
                    if (wasSelected) {
                        for (count = 0; !nextDocument && count < this._tileCount; count++, tile++) {
                            if (tile >= this._tileCount) {
                                tile = 0;
                            }
                            nextDocument = this.getChildInTile(tile).get(TabContainer.SHOWING_CHILD);
                        }
                        if (nextDocument) {
                            this._getProperties(nextDocument).set(LayoutChildProperty.IS_SELECTED, true);
                        }
                    }
                    this._updateBackground();
                    if (this.groupingFacet) {
                        groupId = properties[this.groupingFacet];
                        this._updateGroupRegion(groupId);
                        if (!this.getDocumentCount(groupId) && !(groupProperties && groupProperties.retainEmptySubContainer)) {
                            this._removeGroupWatches(groupId);
                        }
                    }
                }
            }
            if (!this._isTemporaryRemove && properties.get(LayoutChildProperty.DESTROY_ON_CLOSE) !== false) {
                widget.destroyRecursive();
            }
        },

        isChangingTiling: function () {
            return this._isChangingTiling;
        },

        _prepareForGridSizeChange: function (newGridSize) {
            var groupCount = this._getGroupCount();
            var selected = this.getSelectedDocument();
            var saveInfo = this.inherited(arguments);
            saveInfo.groupCount = groupCount;
            saveInfo.selectedDocument = selected;
            saveInfo.oldTileCount = this._tileCount;
            this._clearDocumentSelection(selected);
            return saveInfo;
        },

        _reactToGridSizeChange: function (saveInfo) {
            this.inherited(arguments);
            this._reselectedDocument(saveInfo.selectedDocument);
            this._updateBackground();
        },

        _distributeChildrenFollowingGridSizeChange: function (savedInfo) {
            var that, tile, lastGroupId, groupId, countInTile, childrenInFirstTile;
            if (savedInfo.childInfo.length && savedInfo.groupCount >= this._tileCount) {
                // Add children back - one group per tile, with any excess groups in the last tile
                that = this;
                tile = -1;
                countInTile = 0;
                if (savedInfo.oldTileCount === 1 && this._tileCount === 2) {
                    childrenInFirstTile = this.getChildInTile(0).getChildrenInTabOrder();
                    if (childrenInFirstTile.length) {
                        tile = 0;
                        countInTile = childrenInFirstTile.length;
                        lastGroupId = this._getGroupId(childrenInFirstTile[countInTile - 1]);
                    }
                }
                savedInfo.childInfo.forEach (function (childInfo) {
                    if (childInfo) {
                        groupId = that._getGroupId(childInfo.widget, childInfo.properties);
                        if (groupId !== lastGroupId && tile < that._tileCount - 1) {
                            tile++;
                            countInTile = 0;
                            lastGroupId = groupId;
                        }
                        delete childInfo.properties.index;
                        that._addToTabContainer(tile, childInfo.widget, childInfo.properties, undefined, !countInTile, true);
                        countInTile++;
                    }
                });
            } else {
                this.inherited(arguments);
            }
        },

        _prepareForMerge: function (tileToKeep, tilesToRemove) {
            var selected = this.getSelectedDocument();
            var saveInfo = this.inherited(arguments) || {};
            saveInfo.selectedDocument = selected;
            this._clearDocumentSelection(selected);
            return saveInfo;
        },

        _reactToMerge: function (tileToKeep, tilesToRemove, saveInfo) {
            this.inherited(arguments);
            this._reselectedDocument(saveInfo.selectedDocument);
        },

        _prepareForSplit: function (tile, splitAlong) {
            var selected = this.getSelectedDocument();
            var saveInfo = this.inherited(arguments) || {};
            saveInfo.selectedDocument = selected;
            this._clearDocumentSelection(selected);
            return saveInfo;
        },

        _reactToSplit: function (tile, splitAlong, saveInfo) {
            this.inherited(arguments);
            if (!this._isSplittingForAdd) {
                this._reselectedDocument(saveInfo.selectedDocument);
            }
        },

        eliminateTile: function () {
            var selected = this.getSelectedDocument();
            this._clearDocumentSelection(selected);
            var result = this.inherited(arguments);
            this._reselectedDocument(selected);
            return result;
        },

        getSelectedDocument: function () {
            var selected = this.selectionManager ? this.selectionManager.get(SelectionManager.SELECTED_CHILD) : null;
            if (selected && !this.containsDocument(selected)) {
                selected = null;
            }
            return selected;
        },

        _clearDocumentSelection: function (selected) {
            // Clear the selection so that the selection manager will react when it is restored.
            // Also prevent the selection manager from choosing a successor at this time.
            if (selected && this.selectionManager) {
                this.selectionManager.blockSuccession();
                this.selectionManager.setSelected(selected, false);
                focus.set("activeStack", []);
                this.selectionManager.unblockSuccession();
            }
        },

        _reselectedDocument: function (selected) {
            var that = this;
            if (selected && this.selectionManager) {
                if (this.containsDocument(selected)) {
                    // The previously selected document is still present.  Reselect it.
                    setTimeout(function () {
                        that.selectionManager.setSelected(selected, true);
                    }, 0);
                } else {
                    // TODO: Make a reasonable selection
                }
            }
        },

        _extractToSubContainer: function (children, tile, gridSize) {
            this._getRootContainer()._isChangingTiling = true;
            var that = this;
            var groupId = this._getGroupId(children[0]);
            var groupProperties = this.groupRegistry.getProperties(this.groupingFacet, groupId);

            // Create sub-container with one tile (the desired tiling will be set after children have
            // been added so that the children get distributed properly).
            var subContainer = this._createSubContainer(groupId, groupProperties);
            var subProperties = this._createSubContainerProperties(subContainer, groupId, groupProperties);

            // Add it to the tile from which the menu was launched.  Need to bind properties first because add can
            // trigger getState call.
            that._bindChildProperties(subContainer, subProperties);
            this._addToTabContainer(tile, subContainer, subProperties, undefined, true, true);

            // Remove the group members from their current tab containers
            var removedChildInfo = [];
            this._getTabContainers().forEach(function (tabContainer) {
                that._removeChildrenFromTabContainer(tabContainer, removedChildInfo, function (widget) {
                    return children.indexOf(widget) >= 0;
                });
            });

            // Add the group members to the sub-container's tab container
            removedChildInfo.forEach(function (childInfo) {
                childInfo.properties.index = undefined;
                childInfo.properties.isInGroupTab = true;
                that._transferPropertyBinding(childInfo.widget, childInfo.properties, that, subContainer);
                subContainer._addToTabContainer(0, childInfo.widget, childInfo.properties, undefined, false, true);
            });

            subProperties.set(LayoutChildProperty.IS_SHOWING, true);

            // Tile the sub-container
            subContainer.set(TileContainer.GRID_SIZE, gridSize);
            subContainer._updateBackground();
            this._clearPreMaximizeState();
            this._getRootContainer()._isChangingTiling = false;
            this.emit(LayoutContainerEvent.STATE_CHANGED);
        },

        _createSubContainer: function (groupId, groupProperties) {
            var that = this;
            var subContainer = new DocumentContainer({
                id: that.id + groupId + this._subContainerCount++,
                groupingFacet: groupProperties.subGroupingFacet,
                groupRegistry: this.groupRegistry,
                _containedGroupId: groupId,
                _isChildContainer: true,
                tabPosition: groupProperties.subContainerTabPosition || this.tabPosition,
                animationEnabled: this.animationEnabled
            });
            subContainer.watch(TileContainer.GRID_SIZE, function (name, oldSize, newSize) {
                that._isUpdatingSubContainerGridSize = true;
                groupProperties.set(DocumentGroupProperties.SUB_GRID_SIZE, newSize);
                that._isUpdatingSubContainerGridSize = false;
                if (newSize.w === 1 && newSize.h === 1 && !groupProperties.defaultTiling && !groupProperties.constrainToSubContainer) {
                    that._eliminateSubContainer(subContainer);
                }
            });
            subContainer.on(LayoutContainerEvent.CHILD_REMOVED, function (widget, properties) {
                that.emit(LayoutContainerEvent.CHILD_REMOVED, widget, properties);
                if (subContainer.domNode && subContainer.isEmpty()) {
                    if (!groupProperties.retainEmptySubContainer) {
                        that._eliminateSubContainer(subContainer);
                    }
                }
                // geck: g2265431
                // Remove child from DocumentContainer's map when child is removed from SubContainer
                delete that._widgets[widget.id];
                delete that._properties[widget.id];
            });
            subContainer.on(LayoutContainerEvent.STATE_CHANGED, function () {
                that.emit(LayoutContainerEvent.STATE_CHANGED);
            });

            subContainer.watch(TabContainer.TAB_POSITION, function (name, oldValue) {
                that.notifyStateWatchers(TabContainer.TAB_POSITION, oldValue, that.get(TabContainer.TAB_POSITION));
            });
            return subContainer;
        },

        // Since a group may be distributed across multiple sub-containers each sub-container
        // needs its own properties object (they can't share the group's properties)
        _createSubContainerProperties: function (subContainer, groupId, groupProperties) {
            var that = this;
            var id = groupId + Math.random();
            var properties = new Stateful({title: groupProperties.title,
                                           groupId: groupId, // Causes GroupTabBar to include actions drop down on tab
                                           groupProperties: groupProperties,
                                           childId: id,
                                           setId: id,  // Need set ID for header to appear but must not match document set id
                                           isSubContainer: true});
            subContainer.own(groupProperties.watch(LayoutChildProperty.TITLE, function (name, oldValue, newValue) {
                properties.title = newValue;
            }));
            subContainer.own(properties.watch(LayoutChildProperty.IS_SELECTED, function (name, oldValue, newValue) {
                // The SelectionManager ensures that documents are shown when selected but sub-containers are not
                // registered with the selection manager and therefore must be shown here
                if (newValue) {
                    properties.set(LayoutChildProperty.IS_SHOWING, true);
                    // Select the most recent tab within the sub-container
                    var recent = subContainer._getMostRecent();
                    if (recent.child) {
                        subContainer._getProperties(recent.child).set(LayoutChildProperty.IS_SELECTED, true);
                    }
                }
            }));
            // Forward sub-container maximized and tile changes to group
            subContainer.own(properties.watch(LayoutChildProperty.TILE, function (name, oldValue, newValue) {
                that._tileUpdateDepth++;
                groupProperties.set(LayoutChildProperty.TILE, newValue);
                that._tileUpdateDepth--;
            }));
            subContainer.own(properties.watch(LayoutChildProperty.IS_MAXIMIZED, function (name, oldValue, newValue) {
                that._maximizeUpdateDepth++;
                groupProperties.set(LayoutChildProperty.IS_MAXIMIZED, newValue);
                that._maximizeUpdateDepth--;
            }));

            return properties;
        },

        _addSubContainer: function (groupId, groupProperties, tile) {
            var subContainer = this._createSubContainer(groupId, groupProperties);
            var subProperties = this._createSubContainerProperties(subContainer, groupId, groupProperties);
            if (groupProperties.defaultTiling) {
                subContainer.setState(groupProperties.defaultTiling);
            } else if (groupProperties.subGridSize && (groupProperties.subGridSize.w > 1 || groupProperties.subGridSize.h > 1)) {
                subContainer.set(TileContainer.GRID_SIZE, groupProperties.subGridSize);
            }
            this._bindChildProperties(subContainer, subProperties);
            this._addToTabContainer(tile, subContainer, subProperties, undefined, true, true);
            return subContainer;
        },

        _transferPropertyBinding: function (widget, properties, fromContainer, toContainer) {
            fromContainer._unbindChildProperties(widget, properties);
            toContainer._bindChildProperties(widget, properties);
            if (toContainer.groupingFacet) {
                properties.setId = properties[toContainer.groupingFacet];
            } else {
                delete properties.setId;
            }
        },

        _eliminateSubContainer: function (subContainer, tile) {
            // Pull the documents out of the sub-container
            this._getRootContainer()._isChangingTiling = true;

            var that = this;
            var removedChildInfo = [];

            if (tile === undefined) {
                tile = this._getTileContainingChild(subContainer);
            }

            subContainer._getTabContainers().forEach(function (tabContainer) {
                subContainer._removeChildrenFromTabContainer(tabContainer, removedChildInfo);
            });

            // Add them into this container (same tile as sub-container)
            removedChildInfo.forEach(function (childInfo) {
                that._transferPropertyBinding(childInfo.widget, childInfo.properties, subContainer, that);
                childInfo.properties.index = undefined;
                childInfo.properties.isInGroupTab = false;
                that._addToTabContainer(tile, childInfo.widget, childInfo.properties, undefined, true, true);
            });

            // Remove and destroy the sub-container
            var subProperties = this._getProperties(subContainer);
            this.getChildInTile(tile).removeChild(subContainer);
            this._unbindChildProperties(subContainer, subProperties);
            subContainer.destroyRecursive();
            this._updateBackground();

            // Update the corresponding group properties
            subProperties.groupProperties.set(DocumentGroupProperties.SUB_GRID_SIZE, undefined);
            this.emit(LayoutContainerEvent.STATE_CHANGED);

            this._getRootContainer()._isChangingTiling = false;
        },

        _eliminateEmptySubContainers: function () {
            var that = this;
            this._getChildContainers().forEach(function (subContainer) {
                subContainer._eliminateEmptySubContainers();
                if (subContainer.isEmpty()) {
                    var groupProperties = that._getProperties(subContainer).groupProperties;
                    if (!(groupProperties && groupProperties.retainEmptySubContainer)) {
                        that._eliminateSubContainer(subContainer);
                    }
                }
            });
        },

        _areCorrespondingRegions: function (region1, region2) {
            return (region1 === region2) ||
                this._normalizeRegion(region1) === this._normalizeRegion(region2);
        },

        _normalizeRegion: function (region) {
            if (region === "top") {
                region = "left";
            } else if (region === "bottom") {
                region = "right";
            }
            return region;
        },

        _getRegionFromTile: function (tile) {
            var regions = ["left", "right", "top", "bottom"];
            var region;
            if (this._tileCount <= 2) {
                if (this.gridSize.w > 1) {
                    region = regions[tile];
                } else if (this.gridSize.h > 1) {
                    region = regions[tile + 2];
                } else {
                    region = "center";
                }
            }
            return region;
        },

        _getTileFromRegion: function (region) {
            switch (region) {
                case "top":
                    return 0;
                case "bottom":
                    return 1;
                case "left":
                    return 0;
                case "right":
                    return 1;
            }
            return 0;
        },

        _updatePlusActions: function (container) {
            var showingDocument, groupId, groupProperties, actions, i, wrappedAction, documentContainer;
            var that = this;
            var rootContainer = this._getRootContainer();
            if (!rootContainer) {
                return;
            }
            if (this.showSingleDocumentTab) {
                actions = [];
                showingDocument = container.get(TabContainer.SHOWING_CHILD);
                if (showingDocument && rootContainer.groupingFacet) {
                    groupId = container.getChildProperties(showingDocument)[rootContainer.groupingFacet];
                    groupProperties = this.groupRegistry.getProperties(rootContainer.groupingFacet, groupId);
                    if (groupProperties.plusAction) {
                        actions.push(groupProperties.plusAction)
                    } else if (groupProperties.plusActions) {
                        actions = actions.concat(groupProperties.plusActions);
                    }
                }
                if (actions.length > 0 || !showingDocument) {
                    var hasActionForShowing = actions.length > 0;
                    var groupIds = this.groupRegistry.getIds(rootContainer.groupingFacet);
                    groupIds.forEach(function(groupId) {
                        if (!that._isChildContainer || groupId === that._containedGroupId) {
                            groupProperties = that.groupRegistry.getProperties(rootContainer.groupingFacet, groupId);
                            if (groupProperties.plusAction) {
                                if (groupProperties.isDefault && !hasActionForShowing) {
                                    // Put the default action first
                                    actions.splice(0, 0, groupProperties.plusAction);
                                } else {
                                    actions.push(groupProperties.plusAction);
                                }
                            } else if (groupProperties.plusActions) {
                                if (groupProperties.isDefault && !hasActionForShowing) {
                                    // Put the default actions first
                                    actions = groupProperties.plusActions.slice().concat(actions);
                                } else {
                                    actions = actions.concat(groupProperties.plusActions);
                                }
                            }
                        }
                    });
                }

                // Wrap the action callbacks to set the target subContainer and tile for the newly opened document
                for (i = 0; i < actions.length; i++) {
                    wrappedAction = lang.clone(actions[i]);
                    wrappedAction.callback = lang.partial(function(originalAction) {
                        documentContainer = that;
                        while (documentContainer._isChildContainer) {
                            documentContainer._getParentContainer()._targetSubContainer = documentContainer;
                            documentContainer = documentContainer._getParentContainer();
                        }
                        // Since the plus button was clicked assume that restoration of documents from the
                        // previous session is complete (see geck 1935855).
                        documentContainer.endStateRestore();
                        that._targetTile = that.getTileContaining(container);
                        originalAction.callback.apply(null, Array.prototype.slice.call(arguments, 1));
                    }, actions[i]);
                    actions[i] = wrappedAction;
                }
                container.set(TabContainer.PLUS_ACTIONS, actions);
            }
        },

        _updateMaximizeButton: function (widget) {
            var tabContainer = TabContainerLender.getOwningTabContainer(widget);
            if (tabContainer) {
                var maximizeButton = tabContainer.getUpperRightOverlay();
                if (maximizeButton) {
                    if (this._isRestoringFromMaximize || (!this._isMaximizing && MaximizingContainer.canMaximizeGlobally(widget))) {
                        domClass.replace(maximizeButton, "maximizeButton", "restoreButton");
                        domAttr.set(maximizeButton, "title", layoutL10n.maximize);
                    } else if (this._isMaximizing || (!this._isRestoringFromMaximize && MaximizingContainer.canRestoreGlobally(widget))) {
                        domClass.replace(maximizeButton, "restoreButton", "maximizeButton");
                        domAttr.set(maximizeButton, "title", layoutL10n.restore);
                    }
                }
            }
        },

        _updateBackground: function () {
            if (this.isEmpty() && this._tileCount == 1) {
                domClass.add(this.domNode, "empty");
            } else {
                domClass.remove(this.domNode, "empty");
            }
        },

        _updateRegions: function () {
            // If the container is untiled or split in two, update the region property of each document and
            // also the region for each group
            var that = this;
            var groupRegions = {};
            var tile, tabContainer, region, properties, groupId;
            if (this._tileCount <= 2) {
                this._isUpdatingRegions = true;
                for (tile = 0; tile < this._tileCount; tile++) {
                    region = this._getRegionFromTile(tile);
                    tabContainer = this.getChildInTile(tile);
                    if (tabContainer && !tabContainer.isEmpty()) {
                        tabContainer.getChildrenInTabOrder().forEach(function (child){
                            properties = that._getProperties(child);
                            properties.set(LayoutChildProperty.REGION, region);
                            if (that.groupingFacet) {
                                groupId = properties[that.groupingFacet];
                                if (groupId) {
                                    if (!groupRegions[groupId]) {
                                        groupRegions[groupId] = region;
                                    } else if (region !== groupRegions[groupId]) {
                                        groupRegions[groupId] = "__MIXED__";
                                    }
                                }
                            }
                        });
                    }
                }
                Object.keys(groupRegions).forEach(function (groupId) {
                    region = groupRegions[groupId];
                    if (region === "__MIXED__") {
                        delete that._lastRegionForGroup[groupId];
                    } else if (region !== "center") {
                        that._lastRegionForGroup[groupId] = region;
                    }
                });
                this._isUpdatingRegions = false;
            }
        },

        _updateGroupRegion: function (groupId) {
            var tile, region;
            if (this._closingGroupId !== groupId && this._tileCount == 2) {
                tile = this._getTileContainingGroup(groupId);
                if (tile >= 0) {
                    region = this._getRegionFromTile(tile);
                    this._lastRegionForGroup[groupId] = region;
                } else if (this.getDocumentCount(groupId)) {
                    delete this._lastRegionForGroup[groupId];
                }
            }
        },

        _getPreferredRegion: function (groupId) {
            return this._lastRegionForGroup[groupId] || this.groupRegistry.getProperties(this.groupingFacet, groupId).defaultRegion || "left";
        },

        _updateSelectedGroup: function (groupId) {
            if (groupId !== this._selectedGroupId) {
                if (this._selectedGroupId) {
                    this._getGroupTabs(this._selectedGroupId).forEach(function (tab) {
                        domClass.remove(tab.domNode, "selectedGroup");
                    });
                }
                if (groupId) {
                    this._getGroupTabs(groupId).forEach(function (tab) {
                        domClass.add(tab.domNode, "selectedGroup");
                    });
                }
                this._selectedGroupId = groupId;
            }
        },

        _getGroupTabs: function (groupId) {
            var that = this;
            var widgets = this.getGrandChildren(function(widget, properties) {
                return that._getGroupId(widget, properties) === groupId;
            });
            var tabs = [];
            var tab;
            var i;
            for (i = 0; i < widgets.length; i++) {
                tab = TabContainerLender.getOwningTabContainer(widgets[i]).getTabForChild(widgets[i]);
                if (tab) {
                    tabs.push(tab);
                }
            }
            return tabs;
        },

        layout: function () {
            this.inherited(arguments);
            this._updateRecentDocumentSizes();
        },

        _updateRecentDocumentSizes: function () {
            var rootContainer = this._getRootContainer();
            var borderContainer = LayoutUtils.getAncestorOfClass(this, BorderContainer);
            if ((borderContainer && (borderContainer.isResizingViaDrag() || borderContainer.isAnimating())) ||
                (rootContainer && domClass.contains(rootContainer.domNode, "hideAllChildren"))) {
                return;
            }
            this._getTabContainers().forEach(function (container) {
                var showingChild = container.get(TabContainer.SHOWING_CHILD);
                if (showingChild) {
                    var showingBox = domGeometry.getMarginBox(showingChild.domNode);
                    container.getChildrenInTabOrder().forEach(function (child) {
                        if (!child.isInstanceOf(DocumentContainer)) {
                            var properties = container.getChildProperties(child);
                            properties.__recentWidth = showingBox.w;
                            properties.__recentHeight = showingBox.h;
                        }
                    });
                }
            });
        },

        /*
         * sub-container related add and remove events
         */
        emit: function(type, widget, properties) {
            var parent;
            if ((type !== LayoutContainerEvent.CHILD_ADDED && type !== LayoutContainerEvent.CHILD_REMOVED) ||
                !(properties && properties.isSubContainer)) {
                this.inherited(arguments);
            }
        },

        getProperties: function (widget) {
            return this._getProperties(widget);
        }
    });

    DocumentContainer.TAB_POSITION = TabContainer.TAB_POSITION;
    DocumentContainer.GRID_SIZE = TileContainer.GRID_SIZE;
    DocumentContainer.ROW_WEIGHTS = TileContainer.ROW_WEIGHTS;
    DocumentContainer.COLUMN_WEIGHTS = TileContainer.COLUMN_WEIGHTS;
    DocumentContainer.TILE_COVERAGE = TileContainer.TILE_COVERAGE;
    DocumentContainer.LAST_SELECTED = DocumentGroupProperties.LAST_SELECTED;
    DocumentContainer.DOCUMENT_COUNT = DocumentGroupProperties.DOCUMENT_COUNT;
    DocumentContainer.PLACE_HOLDER_TEXT = "placeHolderText";

    /**
     * @deprecated
     */
    DocumentContainer.ARRANGEMENT = "arrangement";
    /**
     * @deprecated
     */
    DocumentContainer.NO_SPLIT         = 0;
    /**
     * @deprecated
     */
    DocumentContainer.LEFT_RIGHT_SPLIT = 1;
    /**
     * @deprecated
     */
    DocumentContainer.TOP_BOTTOM_SPLIT = 2;
    /**
     * @deprecated
     */
    DocumentContainer.CUSTOM_TILING = 3;

    DocumentContainer.getEnclosingContainer = function (document) {
        var parent = TabContainerLender.getOwningTabContainer(document);
        while (parent && !parent.isInstanceOf(DocumentContainer)) {
            parent = parent.getParent();
        }
        return parent;
    };

    DocumentContainer.validateState = function (state) {
        var result = TileTabContainer.validateState(state);
        if (result.isValid && state.nestings) {
            for (var i = 0; i < state.nestings.length; i++) {
                result = DocumentContainer.validateState(state.nestings[i]);
                if (!result.isValid) {
                    break;
                }
            }
        }
        return result;
    };

    return DocumentContainer;
});
